import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	type CrossFlowViewedScreenNames,
	type InteractedCommonAttributes,
	type ViewedCommonAttributes,
	type ViewedUICommonAttributes,
} from '@atlassian/surface-analytics-specs';

import { SURFACE_ANALYTICS_PRODUCT_MAP } from '../../ccp-products';
import { CROSS_FLOW_CHANNEL, eventTypes } from '../../constants';
import { type RecommendationProductIds } from '../../types';
import { type CrossFlowInteractedUIEvents, type CrossFlowViewedUIEvents } from '../types';

export function useFireCrossFlowAnalyticsEvents() {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const firedViewedScreenEventName = useRef<string>();
	const firedViewedUIEventId = useRef<string>();

	const mapToCcpProductKeys = useCallback(
		(recommendedProductIds: string[], targetProductId?: string): RecommendationProductIds => {
			const ccpProductIds = recommendedProductIds.reduce((acc, curr) => {
				acc.push(SURFACE_ANALYTICS_PRODUCT_MAP.products[curr] ?? curr);
				return acc;
			}, [] as string[]);

			const ccpTargetProductId =
				targetProductId && SURFACE_ANALYTICS_PRODUCT_MAP.products[targetProductId];

			return {
				recommendedProductIds: ccpProductIds,
				targetProductId: ccpTargetProductId ?? targetProductId,
			};
		},
		[],
	);

	const fireViewedScreenEvent = useCallback(
		<Attributes extends Record<string, any>>(
			name: CrossFlowViewedScreenNames,
			commonAttributes: ViewedCommonAttributes,
			attributes?: Attributes,
		): void => {
			// Prevent duplicate events from firing
			if (firedViewedScreenEventName.current === name) {
				return;
			}

			const { recommendedProductIds, ...rest } = commonAttributes;

			const { recommendedProductIds: ccpProductIds } = mapToCcpProductKeys(recommendedProductIds);

			createAnalyticsEvent({
				eventType: eventTypes.SCREEN,
				name,
				attributes: {
					recommendedProductIds: ccpProductIds,
					...rest,
					...attributes,
				},
			}).fire(CROSS_FLOW_CHANNEL);

			// Record the screen name so we don't fire it again
			firedViewedScreenEventName.current = name;
		},
		[createAnalyticsEvent, mapToCcpProductKeys],
	);

	const fireViewedUIEvent = useCallback(
		<Attributes extends Record<string, any>>(
			event: CrossFlowViewedUIEvents,
			commonAttributes: ViewedUICommonAttributes,
			attributes?: Attributes,
		): void => {
			// Prevent duplicate events from firing
			if (firedViewedUIEventId.current === event.actionSubjectId) {
				return;
			}

			const { source, recommendedProductIds, ...rest } = commonAttributes;

			const { recommendedProductIds: ccpProductIds } = mapToCcpProductKeys(recommendedProductIds);

			createAnalyticsEvent({
				eventType: eventTypes.UI,
				...event,
				source,
				attributes: {
					recommendedProductIds: ccpProductIds,
					...rest,
					...attributes,
				},
			}).fire(CROSS_FLOW_CHANNEL);

			firedViewedUIEventId.current = event.actionSubjectId;
		},
		[createAnalyticsEvent, mapToCcpProductKeys],
	);

	const fireInteractedUIEvent = useCallback(
		<Attributes extends Record<string, any>>(
			event: CrossFlowInteractedUIEvents,
			commonAttributes: InteractedCommonAttributes,
			attributes?: Attributes,
		): void => {
			const { source, recommendedProductIds, targetProductId, ...rest } = commonAttributes;
			const { recommendedProductIds: ccpProductIds, targetProductId: ccpTargetProductId } =
				mapToCcpProductKeys(recommendedProductIds ?? [], targetProductId);

			createAnalyticsEvent({
				eventType: eventTypes.UI,
				...event,
				source,
				attributes: {
					recommendedProductIds: ccpProductIds,
					targetProductId: ccpTargetProductId,
					...rest,
					...attributes,
				},
			}).fire(CROSS_FLOW_CHANNEL);
		},
		[createAnalyticsEvent, mapToCcpProductKeys],
	);

	return {
		fireViewedScreenEvent,
		fireViewedUIEvent,
		fireInteractedUIEvent,
	};
}
